import React, { MouseEvent, TouchEvent } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Lens as LensIcon } from '../../icons';
import { OPTION_TYPE, ViewerItemInterface, ViewerOptionInterface } from '../../core';

import styles from './MenuChoiceOptions.module.scss';

export interface MenuChoiceOptionsProps {
  isOpen: boolean;
  options: ViewerOptionInterface[];
  showOptionPreview: (option: ViewerOptionInterface) => void;
  onSelectOptions: (item: ViewerItemInterface) => void;
  optionSelect: (option: ViewerOptionInterface) => void;
}

const previewRegex = new RegExp(`^${OPTION_TYPE.O}\\d`);

const MenuChoiceOptions: React.FC<MenuChoiceOptionsProps> = (props) => {
  const [t] = useTranslation();
  let clickHoldTimer: NodeJS.Timeout | null = null;
  const handleMouseDown = (evt: TouchEvent<HTMLElement>, opt: ViewerOptionInterface) => {
    if (hasPreview(opt) && opt.enabled) {
      evt.preventDefault();
      evt.stopPropagation();
      clickHoldTimer = setTimeout(() => {
        props.showOptionPreview(opt);
      }, 500);
    }
  };

  const handleMouseUp = () => {
    if (clickHoldTimer) clearTimeout(clickHoldTimer);
  };

  const onImageClick = (evt: MouseEvent | null, opt: ViewerOptionInterface): void => {
    if (!('ontouchstart' in window)) {
      if (hasPreview(opt) && opt.enabled) {
        if (evt) {
          evt.preventDefault();
          evt.stopPropagation();
        }
        props.showOptionPreview(opt);
      }
    }
  };

  const hasPreview = (opt: ViewerOptionInterface) => opt && previewRegex.test(opt.id) && opt.imageUrl;

  return (
    <div className={styles.ff_menu_choice_options__container}>
      <div
        className={classnames(styles.ff_menu_choice_options, {
          [styles.ff_menu_choice_options__open]: props.isOpen,
        })}
        data-testid='ff-menu-choice-options'
      >
        {props.options.map((opt) => {
          return (
            <div
              key={`${t(opt.id)}-${opt.name}`}
              onClick={() => props.onSelectOptions(opt)}
              className={classnames(styles.ff_menu_choice_option, {
                [styles.ff_menu_choice_option__selected]: opt.selected,
                [styles.ff_menu_choice_option__disabled]: !opt.enabled,
              })}
            >
              <div
                className={styles.ff_menu_choice_option_image_container}
                onClick={(evt) => onImageClick(evt, opt)}
                onTouchStart={(evt) => handleMouseDown(evt, opt)}
                onTouchEnd={handleMouseUp}
              >
                <div
                  className={styles.ff_menu_choice_option_image}
                  style={{
                    backgroundImage: `url(${opt.imageUrl || opt.missingImage})`,
                  }}
                >
                  {hasPreview(opt) && <LensIcon />}
                </div>
              </div>
              <div className={styles.ff_menu_choice_option_description}>{t(opt.id)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MenuChoiceOptions;
